import React, { useState, useEffect } from 'react';
import { Configuration } from '../BackendApiClient/runtime';
import { ProxiesApi, PersonasApi } from '../BackendApiClient/apis';
import { ProxyResponse, PersonaResponse } from '../BackendApiClient/models';
import { countryCodeToFlag, getProxyDisplayName } from '../utils/proxyDisplay';
import './ProxyManager.css';

const ProxyManager: React.FC = () => {
  const [proxies, setProxies] = useState<ProxyResponse[]>([]);
  const [personas, setPersonas] = useState<PersonaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState<ProxyResponse | null>(null);
  
  // Form states
  const [proxyUrl, setProxyUrl] = useState('');
  const [proxyName, setProxyName] = useState('');
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Bright Data ISP proxy form states
  const [createMode, setCreateMode] = useState<'url' | 'brightdata'>('url');
  const [brightDataProxyName, setBrightDataProxyName] = useState('');
  const [brightDataCountry, setBrightDataCountry] = useState('');
  const [brightDataIpCount, setBrightDataIpCount] = useState(1);
  const [ispCountries, setIspCountries] = useState<{ [key: string]: string | null }>({});
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  // API clients
  const apiConfig = new Configuration({ basePath: '' });
  const proxiesApi = new ProxiesApi(apiConfig);
  const personasApi = new PersonasApi(apiConfig);

  useEffect(() => {
    loadData();
  }, []);

  const loadIspCountries = async () => {
    setLoadingCountries(true);
    try {
      const countries = await proxiesApi.getBrightDataIspCountriesApiProxiesIspCountriesGet({});
      setIspCountries(countries);
    } catch (err) {
      console.error('Failed to load ISP countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [proxiesData, personasData] = await Promise.all([
        proxiesApi.getProxiesApiProxiesGet(),
        fetch('/api/personas').then(res => res.json())
      ]);
      setProxies(proxiesData);
      setPersonas(personasData as PersonaResponse[]);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProxy = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Confirm cost for Bright Data ISP proxies
    if (createMode === 'brightdata') {
      const totalMonthlyCost = brightDataIpCount * 2;
      const confirmed = window.confirm(
        `This will create ${brightDataIpCount} ISP proxy IP(s) at $2/IP/month.\n\n` +
        `Total monthly cost: $${totalMonthlyCost}\n\n` +
        `Do you want to proceed?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    setLoading(true);
    try {
      if (createMode === 'brightdata') {
        const created = await proxiesApi.createBrightDataIspProxyApiProxiesBrightDataIspCreatePost({
          createBrightDataISPProxyRequest: {
            proxyName: brightDataProxyName,
            countryCode: brightDataCountry,
            ipCount: brightDataIpCount
          }
        });
        setProxies([...proxies, created]);
        setShowCreateModal(false);
        setBrightDataProxyName('');
        setBrightDataCountry('');
        setBrightDataIpCount(1);
        setSuccess('Bright Data ISP Proxy created successfully!');
      } else {
        const created = await proxiesApi.createProxyFromUrlApiProxiesPost({
          createProxyFromUrlRequest: { proxyUrl, proxyName: proxyName || undefined }
        });
        setProxies([...proxies, created]);
        setShowCreateModal(false);
        setProxyUrl('');
        setProxyName('');
        setSuccess('Proxy created successfully!');
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(createMode === 'brightdata' ? 'Failed to create Bright Data ISP proxy' : 'Failed to create proxy');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProxy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProxy) return;
    
    setLoading(true);
    setError(null);
    try {
      const updated = await proxiesApi.updateProxyApiProxiesProxyIdPut({
        proxyId: selectedProxy.proxyId,
        updateProxyRequest: { 
          proxyId: selectedProxy.proxyId,
          proxyUrl,
          proxyName: proxyName || undefined
        }
      });
      setProxies(proxies.map(p => p.proxyId === updated.proxyId ? updated : p));
      setShowEditModal(false);
      setSelectedProxy(null);
      setProxyUrl('');
      setProxyName('');
      setSuccess('Proxy updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update proxy');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProxy = async (proxyId: number) => {
    if (!window.confirm('Are you sure you want to delete this proxy? All assigned personas will lose their proxy configuration.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await proxiesApi.deleteProxyApiProxiesProxyIdDelete({ proxyId });
      setProxies(proxies.filter(p => p.proxyId !== proxyId));
      setSuccess('Proxy deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete proxy');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPersonas = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProxy || selectedPersonaIds.length === 0) return;
    
    setLoading(true);
    setError(null);
    try {
      await proxiesApi.addPersonasToProxyApiProxiesProxyIdPersonasPost({
        proxyId: selectedProxy.proxyId,
        requestBody: selectedPersonaIds
      });
      setShowAssignModal(false);
      setSelectedProxy(null);
      setSelectedPersonaIds([]);
      setSuccess(`Assigned ${selectedPersonaIds.length} persona(s) to proxy!`);
      setTimeout(() => setSuccess(null), 3000);
      loadData(); // Reload to get updated persona assignments
    } catch (err) {
      setError('Failed to assign personas to proxy');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (proxy: ProxyResponse) => {
    setSelectedProxy(proxy);
    setProxyUrl(proxy.proxyUrl);
    setProxyName(proxy.proxyName || '');
    setShowEditModal(true);
  };

  const openAssignModal = (proxy: ProxyResponse) => {
    setSelectedProxy(proxy);
    setSelectedPersonaIds([]);
    setShowAssignModal(true);
  };

  const filteredPersonas = personas.filter(p => 
    (p.userId && p.userId.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.demographic && p.demographic.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getProxyPersonaCount = (proxyId: number) => {
    const proxy = proxies.find(p => p.proxyId === proxyId);
    return proxy?.personas?.length || 0;
  };

  return (
    <div className="proxy-manager">
      <div className="page-header">
        <div>
          <h1>Proxy Management</h1>
          <p className="text-secondary">Manage proxy servers for your bot personas</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          Add Proxy
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {loading && proxies.length === 0 ? (
        <div className="loading-state">Loading proxies...</div>
      ) : proxies.length === 0 ? (
        <div className="empty-state">
          <h3>No Proxies Yet</h3>
          <p>Create your first proxy to get started</p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Add Proxy
          </button>
        </div>
      ) : (
        <div className="proxies-grid">
          {proxies.map(proxy => (
            <div key={proxy.proxyId} className="proxy-card card">
              <div className="proxy-header">
                <div className="proxy-title">{getProxyDisplayName(proxy)}</div>
                <div className="proxy-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => openAssignModal(proxy)}
                    title="Assign Personas"
                  >
                    Assign
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEditModal(proxy)}
                    title="Edit"
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleDeleteProxy(proxy.proxyId)}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="proxy-body">
                <div className="proxy-id">
                  <span className="label">ID:</span>
                  <code>{proxy.proxyId}</code>
                </div>

                <div className="proxy-url">
                  <span className="label">URL:</span>
                  <code className="url-display">{proxy.proxyUrl}</code>
                </div>

                <div className="proxy-personas">
                  <span className="label">Assigned Personas:</span>
                  <span className="badge badge-primary">
                    {getProxyPersonaCount(proxy.proxyId)} persona(s)
                  </span>
                </div>

                {proxy.personas && proxy.personas.length > 0 && (
                  <div className="persona-list">
                    {proxy.personas.slice(0, 3).map((personaId, idx) => (
                      <span key={idx} className="persona-pill">{personaId}</span>
                    ))}
                    {proxy.personas.length > 3 && (
                      <span className="persona-pill more">+{proxy.personas.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Proxy Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Proxy</h2>
              <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateProxy}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Creation Method</label>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="createMode"
                        checked={createMode === 'url'}
                        onChange={() => setCreateMode('url')}
                      />
                      Manual URL
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="createMode"
                        checked={createMode === 'brightdata'}
                        onChange={() => {
                          setCreateMode('brightdata');
                          if (Object.keys(ispCountries).length === 0) {
                            loadIspCountries();
                          }
                        }}
                      />
                      Bright Data ISP
                    </label>
                  </div>
                </div>

                {createMode === 'url' ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">Proxy Name</label>
                      <input
                        type="text"
                        value={proxyName}
                        onChange={e => setProxyName(e.target.value)}
                        placeholder="e.g. us_proxy_production"
                      />
                      <p className="form-help">Optional display name for this proxy</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Proxy URL *</label>
                      <input
                        type="text"
                        value={proxyUrl}
                        onChange={e => setProxyUrl(e.target.value)}
                        placeholder="http://[user:pass@]host:port"
                        required
                      />
                      <p className="form-help">Format: http://[username:password@]hostname:port</p>
                      <p className="form-help">Example: http://user:pass@proxy.example.com:8080</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label className="form-label">Proxy Name *</label>
                      <input
                        type="text"
                        value={brightDataProxyName}
                        onChange={e => setBrightDataProxyName(e.target.value)}
                        placeholder="us-proxy-1765792736"
                        required
                      />
                      <p className="form-help">A unique name for your Bright Data ISP proxy zone</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country *</label>
                      {loadingCountries ? (
                        <p>Loading countries...</p>
                      ) : (
                        <div style={{ 
                          border: '1px solid var(--border-default)', 
                          borderRadius: '8px', 
                          overflow: 'hidden',
                          background: 'var(--bg-secondary)'
                        }}>
                          {/* Collapsed view / Toggle button */}
                          <div 
                            onClick={() => setCountryPickerOpen(!countryPickerOpen)}
                            style={{ 
                              padding: '0.75rem 1rem', 
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: 'var(--bg-primary)',
                              borderBottom: countryPickerOpen ? '1px solid var(--border-default)' : 'none'
                            }}
                          >
                            {brightDataCountry ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>{countryCodeToFlag(brightDataCountry)}</span>
                                <strong>{Object.entries(ispCountries).find(([_, code]) => code === brightDataCountry)?.[0]}</strong>
                                <span style={{ opacity: 0.6 }}>({brightDataCountry})</span>
                              </div>
                            ) : (
                              <span style={{ opacity: 0.6 }}>Select a country...</span>
                            )}
                            <span style={{ 
                              transform: countryPickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>▼</span>
                          </div>

                          {/* Expanded view */}
                          {countryPickerOpen && (
                            <>
                              <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                                <input
                                  type="text"
                                  value={countrySearch}
                                  onChange={e => setCountrySearch(e.target.value)}
                                  placeholder="🔍 Search countries..."
                                  style={{ 
                                    width: '100%', 
                                    margin: 0,
                                    background: 'var(--bg-primary)',
                                    border: '1px solid var(--border-default)',
                                    borderRadius: '6px',
                                    padding: '0.5rem 0.75rem'
                                  }}
                                  autoFocus
                                />
                              </div>
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {Object.entries(ispCountries)
                                  .filter(([name, code]) => 
                                    name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                    (code && code.toLowerCase().includes(countrySearch.toLowerCase()))
                                  )
                                  .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                                  .map(([name, code]) => (
                                    <div
                                      key={code}
                                      onClick={() => {
                                        setBrightDataCountry(code || '');
                                        setCountryPickerOpen(false);
                                        setCountrySearch('');
                                      }}
                                      style={{
                                        padding: '0.6rem 1rem',
                                        cursor: 'pointer',
                                        background: brightDataCountry === code ? 'var(--accent-primary)' : 'transparent',
                                        color: brightDataCountry === code ? 'white' : 'inherit',
                                        borderBottom: '1px solid var(--border-default)',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'background 0.15s ease'
                                      }}
                                      onMouseEnter={e => {
                                        if (brightDataCountry !== code) {
                                          e.currentTarget.style.background = 'var(--bg-hover)';
                                        }
                                      }}
                                      onMouseLeave={e => {
                                        if (brightDataCountry !== code) {
                                          e.currentTarget.style.background = 'transparent';
                                        }
                                      }}
                                    >
                                      <span>{countryCodeToFlag(code)} {name}</span>
                                      <span style={{ 
                                        opacity: 0.7, 
                                        fontSize: '0.85rem',
                                        fontFamily: 'monospace'
                                      }}>{code}</span>
                                    </div>
                                  ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Number of IPs *</label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={brightDataIpCount}
                        onChange={e => setBrightDataIpCount(parseInt(e.target.value) || 1)}
                        required
                      />
                      <p className="form-help">Number of static IPs to allocate (1-100)</p>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={loading || (createMode === 'url' ? !proxyUrl : (!brightDataProxyName || !brightDataCountry))}
                >
                  {loading ? 'Creating...' : (createMode === 'brightdata' ? 'Create Bright Data Proxy' : 'Create Proxy')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Proxy Modal */}
      {showEditModal && selectedProxy && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Proxy #{selectedProxy.proxyId}</h2>
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateProxy}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Proxy Name</label>
                  <input
                    type="text"
                    value={proxyName}
                    onChange={e => setProxyName(e.target.value)}
                    placeholder="e.g. us_proxy_production"
                  />
                  <p className="form-help">Optional display name for this proxy</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Proxy URL *</label>
                  <input
                    type="text"
                    value={proxyUrl}
                    onChange={e => setProxyUrl(e.target.value)}
                    placeholder="http://[user:pass@]host:port"
                    required
                  />
                  <p className="form-help">Format: http://[username:password@]hostname:port</p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading || !proxyUrl}>
                  {loading ? 'Updating...' : 'Update Proxy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Personas Modal */}
      {showAssignModal && selectedProxy && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Personas to Proxy #{selectedProxy.proxyId}</h2>
              <button className="btn btn-ghost" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <form onSubmit={handleAssignPersonas}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Search Personas</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search by ID or demographic..."
                  />
                </div>

                <div className="personas-selection">
                  {filteredPersonas.length === 0 ? (
                    <div className="empty-state-small">
                      <p>No personas found</p>
                    </div>
                  ) : (
                    filteredPersonas.map(persona => (
                      <div key={persona.userId} className="persona-select-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedPersonaIds.includes(persona.userId)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedPersonaIds([...selectedPersonaIds, persona.userId]);
                              } else {
                                setSelectedPersonaIds(selectedPersonaIds.filter(id => id !== persona.userId));
                              }
                            }}
                          />
                          <div className="persona-info-assign">
                            <code>{persona.userId}</code>
                            {persona.demographic && (
                              <span className="text-muted">{persona.demographic}</span>
                            )}
                            {persona.region && (
                              <span className="badge badge-secondary">{persona.region.carrierRegion}</span>
                            )}
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>

                {selectedPersonaIds.length > 0 && (
                  <div className="selection-summary">
                    <strong>{selectedPersonaIds.length}</strong> persona(s) selected
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || selectedPersonaIds.length === 0}
                >
                  {loading ? 'Assigning...' : `Assign ${selectedPersonaIds.length} Persona(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxyManager;

